import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import qs from 'query-string'
import { useHistory, useLocation } from 'react-router-dom'

import RequestPasswordResetForm from './request-password-reset-form'
import Button from '../button'
import styles from './request-password-reset.css'

import { getModalLocation } from '../../lib/modal'
import { MODALS } from '../../constants'
import { E2E_SELECTORS } from '../../e2e-selectors'

/**
 * Entry point for the "forgot password => password reset" journey, triggered by URL param `modal=passwordForgotten`.
 * Renders the initial modal with the "Reset your password" hero, drives the email submission form,
 * and swaps to the confirmation message once the request succeeds.
 */
export default function RequestPasswordReset() {
  const location = useLocation()
  const history = useHistory()
  const parsedSearch = qs.parse(location && location.search)
  const initialEmail = (parsedSearch && parsedSearch.email) || ''

  const [email, setEmail] = useState(initialEmail)
  const [isRequestDone, setIsRequestDone] = useState(false)

  const onPasswordRequestSuccess = useCallback((submittedEmail) => {
    setIsRequestDone(true)
    setEmail(submittedEmail)
  }, [])

  const onLoginClick = useCallback(() => {
    history.push(getModalLocation(location, MODALS.qsParams.login))
  }, [history, location])

  return (
    <div>
      {isRequestDone ? (
        <RequestConfirmation
          email={email}
          onLoginClick={onLoginClick}
        />
      ) : (
        <RequestFormView
          email={email}
          onRequestSuccess={onPasswordRequestSuccess}
        />
      )}
    </div>
  )
}

// Primary form view prompting for email before sending the reset link.
function RequestFormView({ email, onRequestSuccess }) {
  return (
    <div className={styles.formWrapper}>
      <div className={styles.formHeader}>
        <h1 className={styles.formTitle}>
          Reset your password
        </h1>

        <span className={styles.formSubHeader}>
          Forgot your password? It happens.
          Please enter the email associated with your account
          and we&apos;ll send you a password reset link.
        </span>
      </div>

      <RequestPasswordResetForm
        email={email}
        onRequestSuccess={onRequestSuccess}
      />
    </div>
  )
}
RequestFormView.propTypes = {
  email: PropTypes.string.isRequired,
  onRequestSuccess: PropTypes.func.isRequired
}

// Confirmation screen shown after a reset email has been sent successfully.
function RequestConfirmation({ email, onLoginClick }) {
  return (
    <div className={styles.confirmationWrapper}>
      <div className={styles.confirmationHeader}>
        <h1 className={styles.confirmationTitle}>
          Next step, check your email
        </h1>
      </div>

      <p className={styles.confirmationDescription}>
        Go to your email and follow the instructions in the email we&apos;ve just
        sent you. If you haven&apos;t received the email, make sure to check your
        junk, spam or promotions folder. The email was sent to this address:
      </p>

      <p className={styles.confirmationEmail}>{email}</p>

      <Button
        className={styles.confirmationButton}
        e2eIdentifier={E2E_SELECTORS.RESET_SENT_LOGIN_BUTTON}
        text="Log in"
        onClick={onLoginClick}
      />
    </div>
  )
}
RequestConfirmation.propTypes = {
  email: PropTypes.string.isRequired,
  onLoginClick: PropTypes.func.isRequired
}
